import React, { useEffect, useState } from "react";
import { Layout, Space, Col, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import BankCustomerBanner from "../../../components/header/bankCustomerBanner";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import { documentTitle } from "../../../components/header/documentTitle";
import HoldingEntityTable from "../../../components/specific/company/holdingEntities/holdingEntityTable";
import HideOnMobile from "../../../components/generic/hideOnMobile";
import AddEntitiesCsvButton from "../../../components/specific/company/addEntityDetails/addEntitiesCsvButton";
import AddEntityDetailsButton from "../../../components/specific/company/addEntityDetails/generic";
import EntityPaymentsApplicationModal from "../../../components/specific/company/holdingEntities/EntityPaymentsApplicationModal";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import { Button, Box, Stack } from '@mui/material';
import MergeEntitiesModal from "../../../components/specific/company/holdingEntities/mergeEntitiesModal";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";
import PermissionGate from '../../../helpers/PermissionGate'

function HoldingEntities() {
  const [entityData, setEntityData] = useState([]);
  const [showEntities, setShowEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [entitiesCount, setEntitiesCount] = useState(-1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [applicationModalVisible, setApplicationModalVisible] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(0);

  const [mergeEntitiesModalVisible, setMergeEntitiesModalVisible] = useState(false);


  const entityHelper = (d, cached = false) => {
    var entities = d['entities'];

    setShowEntities(prevState => {
      const entityMap = new Map(prevState.map(entity => [entity.id, entity]));
      entities.forEach(entity => {
        if (!cached || !entityMap.has(entity.id)) {
          entityMap.set(entity.id, entity);
        }
      });
      return Array.from(entityMap.values());
    });

    setEntityData(prevState => {
      const entityMap = new Map(prevState.map(entity => [entity.id, entity]));
      entities.forEach(entity => {
        if (!cached || !entityMap.has(entity.id)) {
          entityMap.set(entity.id, entity);
        }
      });
      return Array.from(entityMap.values());
    });
  };

  useEffect(() => {
    documentTitle();
  }, [])

  useEffect(() => {
    backendGetRequest("/api/entity", { params: { 'page_index': 0 } }, "")
      .then((res) => {
        setEntitiesCount(res?.data?.total_records);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("/api/entity", { params: { 'page_index': i } },
            (cachedResponse) => {
              if (cachedResponse?.data) {
                entityHelper(cachedResponse?.data, true);
              }
            },
            (nonCachedResponse) => {
              if (nonCachedResponse?.data) {
                entityHelper(nonCachedResponse?.data, false);
              }
            });
        }
      });
  }, []);


  useEffect(() => {
    if (entitiesCount < 0) return
    setLoadingEntities(entitiesCount > entityData.length);
  }, [entityData, entitiesCount])


  const handleSearch = (e) => {
    if (e.target.value === "") {
      setShowEntities([...entityData]);
    } else {
      var search_text = e.target.value;
      var data = [...entityData];
      var resData = [];
      for (var i = 0; i < data.length; i++) {
        var entityName = data[i].entity_info.name;
        var entityType = data[i].entity_info.entity_type;
        var entityStateOfIncorporation = data[i].entity_info.state_of_incorporation;
        var entityStatus = data[i].entity_info.status;
        if (
          entityName.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
          entityType.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
          entityStateOfIncorporation.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
          entityStatus.toLowerCase().indexOf(search_text.toLowerCase()) > -1
        ) {
          resData.push(data[i]);
        }
      }
      setShowEntities(resData);
    }
  };

  const addEntities = (newEntities) => {
    entityHelper({ entities: newEntities });
  }

  const updateEntityData = (entityId, updatedEntity) => {
    setEntityData(prevState => {
      const newEntities = prevState.map(entity => {
        if (entity.id === entityId) {
          return updatedEntity; // Replace entire entity with updated data
        }
        return entity;
      });
      return newEntities;
    });
    setShowEntities(prevState => {
      const newEntities = prevState.map(entity => {
        if (entity.id === entityId) {
          return updatedEntity; // Replace entire entity with updated data
        }
        return entity;
      });
      return newEntities;
    });
  }

  const deleteEntity = (entityId) => {
    setEntityData(prevState => {
      const newEntities = prevState.filter(entity => entity.id !== entityId);
      return newEntities;
    });
    setShowEntities(prevState => {
      const newEntities = prevState.filter(entity => entity.id !== entityId);
      return newEntities;
    });
    setEntitiesCount(prevState => prevState - 1);
  }

  const updateEntities = (prevState, parentEntityId, sumBankAccountCounts, mergeEntityIds) => {
    return prevState.filter(entity => !mergeEntityIds.includes(entity.id) || entity.id === parentEntityId)
      .map(entity => {
        if (entity.id === parentEntityId) {
          return { ...entity, bank_account_count: sumBankAccountCounts };
        }
        return entity;
      });
  };

  const mergeEntities = (mergeEntityIds, parentEntityId) => {
    const sumBankAccountCounts = entityData.reduce((sum, entity) => {
      if (mergeEntityIds.includes(entity.id)) {
        return sum + entity.bank_account_count;
      }
      return sum;
    }, 0);
    setEntityData(prevState => updateEntities(prevState, parentEntityId, sumBankAccountCounts, mergeEntityIds));
    setShowEntities(prevState => updateEntities(prevState, parentEntityId, sumBankAccountCounts, mergeEntityIds));
    setEntitiesCount(prevState => prevState - mergeEntityIds?.length + 1);
  };


  return (
    <OuterBox>

      <BankCustomerBanner
        padding={0}
        paddingBottom={0}
        margin="0px"
      />

      <HeaderBox
        title="Holding Entities"
        actions={<HideOnMobile>
          <Space>
            <PermissionGate
              requiredRole='dashboardEditor'
              toolTipText='Only admins and dashboard editors may add new holding entities'
              readOnly={
                <Button
                  fullWidth
                  size="small"
                  disabled
                  variant="contained"
                  sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                >
                  + Add New
                </Button>
              }
            >
              <AddEntityDetailsButton
                buttonTitle={"+ Add New"}
                newEntitySource={"MA"}
                addEntities={addEntities}
              />
            </PermissionGate>
            <PermissionGate
              requiredRole='dashboardEditor'
              toolTipText='Only admins and dashboard editors may add new holding entities'
              readOnly={
                <Button
                  fullWidth
                  size="small"
                  disabled
                  variant="contained"
                  sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                >
                  Upload CSV
                </Button>
              }
            >
              <AddEntitiesCsvButton />
            </PermissionGate>
            <PermissionGate
              requiredRole='dashboardEditor'
              toolTipText='Only admins and dashboard editors may merge holding entities'
              readOnly={
                <Button
                  fullWidth
                  size="small"
                  disabled
                  variant="contained"
                  sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                >
                  Merge
                </Button>
              }
            >
              <Button
                disableElevation fullWidth size="small" variant="outlined" color="primary" sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                onClick={selectedRows.length === 0 ? undefined : () => setMergeEntitiesModalVisible(true)}
              >
                Merge
              </Button>
            </PermissionGate>
          </Space>
        </HideOnMobile>}
      />

      <Input
        disabled={loadingEntities}
        placeholder="Search"
        prefix={<SearchOutlined />}
        onChange={handleSearch}
      />
      <HoldingEntityTable
        entityData={showEntities}
        newEntitySource={"MA"}
        loading={loadingEntities}
        numerator={entityData.length}
        denominator={entitiesCount}
        updateEntityData={updateEntityData}
        deleteEntity={deleteEntity}
        setSelectedRows={(rows) => setSelectedRows(rows)}
        setApplicationModalVisible={setApplicationModalVisible}
        setSelectedEntity={setSelectedEntity}
      />
      {applicationModalVisible && (
        <EntityPaymentsApplicationModal
          visible={applicationModalVisible}
          handleCancelModal={() => setApplicationModalVisible(false)}
          entityId={selectedEntity}
        />
      )}
      {mergeEntitiesModalVisible && (
        <MergeEntitiesModal
          visible={mergeEntitiesModalVisible}
          selectedRows={selectedRows}
          mergeEntities={mergeEntities}
          handleCancelModal={() => setMergeEntitiesModalVisible(false)}
          handleShowModal={() => setMergeEntitiesModalVisible(true)}
        />
      )}
    </OuterBox>
  );
}

export default HoldingEntities;
